import { useSite } from '@/hooks/use-site'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@blogging/ui'
import { ShieldAlert } from 'lucide-react'
import { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

interface AdminGuardProps {
  children: ReactNode
  fallback?: ReactNode
}

export function AdminGuard({ children, fallback }: AdminGuardProps) {
  const { isAdmin, isLoading, siteId } = useSite()
  const navigate = useNavigate()

  if (isLoading) {
    return null // Or show a loading skeleton
  }

  if (!isAdmin) {
    if (fallback) {
      return <>{fallback}</>
    }

    // Default unauthorized page
    return (
      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mx-auto mb-4">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold">Access Denied</CardTitle>
            <CardDescription>
              You don't have permission to access this page. Admin access is
              required.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
              <p className="font-medium mb-2">Why am I seeing this?</p>
              <p className="text-xs">
                This page contains site settings and management features that
                are only available to administrators. If you believe you should
                have access, please contact your site administrator.
              </p>
            </div>
            <Button onClick={() => navigate(`/${siteId}`)} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}
